<template>
  <div class="weekly-test-data-view">
    <div class="section-header">
      <h3>周测数据查询</h3>
      <div class="filter-controls">
        <el-select 
          v-model="selectedTest" 
          placeholder="选择周测" 
          clearable
          @change="handleTestChange"
          style="width: 200px; margin-right: 10px;"
        >
          <el-option
            v-for="test in tests"
            :key="test.test_id"
            :label="test.test_name"
            :value="test.test_id">
          </el-option>
        </el-select>
        <el-button type="primary" @click="loadData" :loading="loading">
          <i class="el-icon-search"></i> 查询
        </el-button>
      </div>
    </div>

    <div class="data-table-container">
      <el-table 
        :data="tableData" 
        v-loading="loading"
        stripe
        border
        style="width: 100%"
        :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
      >
        <el-table-column prop="test_name" label="测试名称" width="200">
          <template slot-scope="scope">
            <el-tag type="info" size="small">{{ scope.row.test_name }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="score" label="测试分数" width="120" align="center">
          <template slot-scope="scope">
            <span class="score-value" :class="getScoreClass(scope.row.score)">
              {{ scope.row.score }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="test_date" label="测试日期" width="120" align="center"></el-table-column>
        <el-table-column prop="created_at" label="记录时间" width="160" align="center"></el-table-column>
        <el-table-column prop="test_description" label="测试描述" min-width="200">
          <template slot-scope="scope">
            <span class="description-text">
              {{ scope.row.test_description || '无描述' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100" align="center">
          <template slot-scope="scope">
            <el-button 
              type="text" 
              size="small" 
              @click="showDetail(scope.row)"
            >
              详情
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total">
        </el-pagination>
      </div>
    </div>

    <!-- 详情对话框 -->
    <el-dialog title="周测成绩详情" :visible.sync="detailDialogVisible" width="600px">
      <div v-if="selectedRecord" class="detail-content">
        <div class="detail-item">
          <label>测试名称:</label>
          <span>{{ selectedRecord.test_name }}</span>
        </div>
        <div class="detail-item">
          <label>测试分数:</label>
          <span class="score-highlight" :class="getScoreClass(selectedRecord.score)">
            {{ selectedRecord.score }}
          </span>
        </div>
        <div class="detail-item">
          <label>测试日期:</label>
          <span>{{ selectedRecord.test_date }}</span>
        </div>
        <div class="detail-item">
          <label>记录时间:</label>
          <span>{{ selectedRecord.created_at }}</span>
        </div>
        <div class="detail-item" v-if="selectedRecord.test_description">
          <label>测试描述:</label>
          <p class="description-text">{{ selectedRecord.test_description }}</p>
        </div>
      </div>
    </el-dialog>

  </div>
</template>

<script>
import { getWeeklyTestData, getWeeklyTests } from '../../../api/dataQueryApi'

export default {
  name: 'WeeklyTestDataView',
  data() {
    return {
      loading: false,
      tableData: [],
      tests: [],
      selectedTest: null,
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      detailDialogVisible: false,
      selectedRecord: null
    }
  },
  mounted() {
    this.loadTests()
    this.loadData()
  },
  watch: {
    tableData: {
      handler() {
        this.$nextTick(() => {
          this.renderChart()
        })
      },
      deep: true
    }
  },
  methods: {
    async loadTests() {
      try {
        const response = await getWeeklyTests()
        this.tests = response.data
      } catch (error) {
        console.error('获取周测列表失败:', error)
      }
    },
    async loadData() {
      try {
        this.loading = true
        const params = {
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        }
        
        if (this.selectedTest) {
          params.test_id = this.selectedTest
        }
        
        const response = await getWeeklyTestData(params)
        this.tableData = response.data.results
        this.pagination.total = response.data.total
      } catch (error) {
        console.error('获取周测数据失败:', error)
        this.$message.error(error.message || '获取周测数据失败')
      } finally {
        this.loading = false
      }
    },
    handleTestChange() {
      this.pagination.page = 1
      this.loadData()
    },
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.pagination.page = 1
      this.loadData()
    },
    handleCurrentChange(val) {
      this.pagination.page = val
      this.loadData()
    },
    showDetail(row) {
      this.selectedRecord = row
      this.detailDialogVisible = true
    },
    getScoreClass(score) {
      if (score >= 90) {
        return 'score-excellent'
      } else if (score >= 80) {
        return 'score-good'
      } else if (score >= 60) {
        return 'score-pass'
      } else {
        return 'score-fail'
      }
    },
    renderChart() {
      if (this.tableData.length === 0) return
      
      // 简单的图表渲染逻辑，这里可以集成 ECharts 或其他图表库
      const chartContainer = document.getElementById('weeklyTestChart')
      if (!chartContainer) return
      
      // 这里应该使用真正的图表库，比如 ECharts
      chartContainer.innerHTML = `
        <div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #666;">
          <p>图表功能需要集成 ECharts 库</p>
        </div>
      `
    }
  }
}
</script>

<style scoped>
.weekly-test-data-view {
  padding: 0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #ebeef5;
}

.section-header h3 {
  color: #333;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.filter-controls {
  display: flex;
  align-items: center;
}

.data-table-container {
  background: white;
}

.score-value {
  font-weight: 600;
  font-size: 16px;
}

.score-excellent {
  color: #67c23a;
}

.score-good {
  color: #409eff;
}

.score-pass {
  color: #e6a23c;
}

.score-fail {
  color: #f56c6c;
}

.description-text {
  color: #666;
  line-height: 1.4;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

.detail-content {
  padding: 10px 0;
}

.detail-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-item label {
  width: 100px;
  color: #666;
  font-weight: 500;
  flex-shrink: 0;
}

.detail-item span {
  color: #333;
  flex: 1;
}

.score-highlight {
  font-size: 18px;
  font-weight: 600;
}

.chart-container {
  margin-top: 30px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.chart-container h4 {
  color: #333;
  margin-bottom: 20px;
  font-size: 16px;
  font-weight: 600;
}

@media (max-width: 768px) {
  .section-header {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }
  
  .filter-controls {
    justify-content: center;
  }
  
  .detail-item {
    flex-direction: column;
    gap: 5px;
  }
  
  .detail-item label {
    width: auto;
  }
}
</style>
